import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import FileUpload from './FileUpload';
import { toast } from 'sonner';
import { fetchJsonOrThrow, getApiErrorMessage } from '../lib/apiError';
import { Search, User, FileText, Calendar, IndianRupee, Loader2, Scissors, Plus, Trash2 } from 'lucide-react';


const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';


const getToken = () => localStorage.getItem('admin_token');

const getLocalDateInputValue = () => {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 10);
};


// Debounce hook for search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};


// ── Skeleton / loader helpers ───────────────────────────────────────────────

const ShimmerLoader = ({ className = "" }) => (
  <div className={`relative overflow-hidden bg-gray-200 rounded-lg ${className}`}>
    <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-gray-200 via-white to-gray-200" />
  </div>
);

const StudentCardSkeleton = () => (
  <div className="flex items-center p-3 border rounded-lg animate-pulse">
    <div className="h-10 w-10 bg-gray-300 rounded-full mr-3 animate-pulse" />
    <div className="flex-1 space-y-2">
      <ShimmerLoader className="h-4 w-3/4" />
      <ShimmerLoader className="h-3 w-1/2" />
    </div>
  </div>
);

const InvoiceCardSkeleton = () => (
  <div className="p-4 border rounded-lg animate-pulse space-y-3">
    <div className="flex items-start justify-between">
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 bg-gray-300 rounded" />
          <ShimmerLoader className="h-4 w-2/3" />
        </div>
        <ShimmerLoader className="h-3 w-1/2" />
        <ShimmerLoader className="h-3 w-1/3" />
      </div>
      <div className="text-right space-y-2">
        <ShimmerLoader className="h-4 w-20" />
        <ShimmerLoader className="h-3 w-16" />
      </div>
    </div>
  </div>
);

const InstallmentCardSkeleton = () => (
  <div className="p-4 border rounded-lg animate-pulse">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3 flex-1">
        <div className="w-8 h-8 bg-blue-200 rounded-full animate-pulse" />
        <div className="flex-1 space-y-2">
          <ShimmerLoader className="h-4 w-32" />
          <div className="flex items-center gap-4">
            <ShimmerLoader className="h-3 w-24" />
            <ShimmerLoader className="h-3 w-24" />
          </div>
        </div>
      </div>
      <div className="text-right space-y-2">
        <ShimmerLoader className="h-4 w-20" />
        <ShimmerLoader className="h-3 w-16" />
      </div>
    </div>
  </div>
);

const ExpandingCircleLoader = () => (
  <div className="flex items-center justify-center">
    <div className="relative w-16 h-16">
      <div className="absolute inset-0 border-4 border-blue-200 rounded-full animate-ping" />
      <div className="absolute inset-0 border-4 border-blue-600 rounded-full animate-pulse" />
      <div className="absolute inset-0 flex items-center justify-center">
        <IndianRupee className="h-6 w-6 text-blue-600" />
      </div>
    </div>
  </div>
);


// ── Main component ──────────────────────────────────────────────────────────

const RecordPaymentDialog = ({
  isOpen,
  onClose,
  initialData = {},
  onPaymentRecorded,
  preSelectedDue = null,   // { studentId, studentName, invoiceId, installmentNo, amount }
}) => {
  const [step, setStep] = useState('student');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [students, setStudents] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [searching, setSearching] = useState(false);

  // Split-installment state
  const [splitTarget, setSplitTarget] = useState(null);   // the installment being split
  const [splitInvoice, setSplitInvoice] = useState(null); // invoice it belongs to
  const [splitRows, setSplitRows] = useState([
    { amount: '', dueDate: '' },
    { amount: '', dueDate: '' },
  ]);
  const [splitting, setSplitting] = useState(false);

  const [paymentData, setPaymentData] = useState({
    invoiceId: '',
    installmentNo: '',
    studentId: '',
    amount: '',
    paymentDate: getLocalDateInputValue(),
    mode: 'upi',
    referenceNo: '',
    notes: '',
    receiptUrl: '',
    ...initialData
  });

  // Keep a ref so that async callbacks inside fetchStudentInvoices can read
  // the latest selectedStudent value even before the state flush.
  const selectedStudentRef = useRef(null);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // ── Auto pre-select when opened from a due-payment row ──────────────────
  useEffect(() => {
    if (!isOpen || !preSelectedDue) return;

    const student = {
      _id:   preSelectedDue.studentId,
      name:  preSelectedDue.studentName,
      email: preSelectedDue.studentEmail || '',
      phone: preSelectedDue.studentPhone || '',
    };
    selectedStudentRef.current = student;
    setSelectedStudent(student);
    setSearchQuery('');
    setStudents([]);

    fetchStudentInvoices(
      preSelectedDue.studentId,
      preSelectedDue.invoiceId,
    );
  }, [isOpen]); // intentionally omits preSelectedDue — we only want this to fire on open/close

  // ── Student search ───────────────────────────────────────────────────────
  useEffect(() => {
    const searchStudents = async () => {
      if (debouncedSearchQuery.length < 2) {
        setStudents([]);
        setSearching(false);
        return;
      }
      setSearching(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/fees/search-students?query=${encodeURIComponent(debouncedSearchQuery)}`,
          { headers: { 'Authorization': `Bearer ${getToken()}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setStudents(data.students || []);
        }
      } catch (error) {
        console.error('Error searching students:', error);
        setStudents([]);
      } finally {
        setSearching(false);
      }
    };
    searchStudents();
  }, [debouncedSearchQuery]);

  // ── Fetch invoices (with optional auto-selection) ────────────────────────
  const fetchStudentInvoices = async (
    studentId,
    autoInvoiceId = null,
  ) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/fees/student-invoices/${studentId}`,
        { headers: { 'Authorization': `Bearer ${getToken()}` } }
      );
      const data = await fetchJsonOrThrow(response, 'Failed to fetch student invoices');
      const invoiceList = data.invoices || [];
      setInvoices(invoiceList);

      if (autoInvoiceId) {
        const matchingInvoice = invoiceList.find(
          inv => inv._id.toString() === autoInvoiceId.toString()
        );
        if (matchingInvoice) {
          setSelectedInvoice(matchingInvoice);
          // Always land on the installment step so the user can see and
          // optionally split installments before recording payment.
          setStep('installment');
          return;
        }
      }
      setStep('invoice');
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error(getApiErrorMessage(error, 'Failed to fetch student invoices'));
    } finally {
      setLoading(false);
    }
  };

  // ── Step handlers ────────────────────────────────────────────────────────
  const handleStudentSelect = (student) => {
    selectedStudentRef.current = student;
    setSelectedStudent(student);
    setSearchQuery('');
    setStudents([]);
    fetchStudentInvoices(student._id);
  };

  const handleInvoiceSelect = (invoice) => {
    setSelectedInvoice(invoice);
    setStep('installment');
  };

  const handleInstallmentSelect = (installment, invoice, studentId = null) => {
    setSelectedInstallment(installment);
    const sid = studentId || selectedStudentRef.current?._id || selectedStudent?._id;
    setPaymentData(prev => ({
      ...prev,
      studentId:     sid,
      invoiceId:     invoice._id,
      installmentNo: installment.installmentNo,
      amount:        installment.dueAmount.toString(),
    }));
    setStep('payment');
  };

  // ── Split handlers ───────────────────────────────────────────────────────
  const openSplitStep = (installment, invoice) => {
    setSplitTarget(installment);
    setSplitInvoice(invoice);
    const half = (installment.dueAmount / 2).toFixed(2);
    setSplitRows([
      { amount: half, dueDate: '' },
      { amount: (installment.dueAmount - parseFloat(half)).toFixed(2), dueDate: '' },
    ]);
    setStep('split');
  };

  const addSplitRow = () =>
    setSplitRows(prev => [...prev, { amount: '', dueDate: '' }]);

  const removeSplitRow = (idx) =>
    setSplitRows(prev => prev.filter((_, i) => i !== idx));

  const updateSplitRow = (idx, field, value) =>
    setSplitRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));

  const splitTotal = splitRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const splitDiff  = splitTarget ? Math.abs(splitTotal - splitTarget.dueAmount) : 0;
  const splitValid = splitDiff < 0.01;

  const handleSplitSubmit = async () => {
    if (!splitValid) {
      toast.error(`Split amounts must total ₹${splitTarget.dueAmount.toLocaleString('en-IN')}`);
      return;
    }
    for (const row of splitRows) {
      if (!row.dueDate) { toast.error('Please set a due date for every split'); return; }
      if (!row.amount || parseFloat(row.amount) <= 0) {
        toast.error('Each split amount must be greater than 0'); return;
      }
    }

    try {
      setSplitting(true);
      const response = await fetch(
        `${API_BASE_URL}/fees/invoices/${splitInvoice._id}/split-installment`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            installmentNo: splitTarget.installmentNo,
            splits: splitRows.map(r => ({ amount: parseFloat(r.amount), dueDate: r.dueDate })),
          }),
        }
      );
      await fetchJsonOrThrow(response, 'Failed to split installment');
      toast.success(`Installment #${splitTarget.installmentNo} split successfully`);

      // Reload the installments for this student and return to installment step
      setSplitTarget(null);
      setSplitInvoice(null);
      const sid = selectedStudentRef.current?._id || selectedStudent?._id;
      await fetchStudentInvoices(sid);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to split installment'));
    } finally {
      setSplitting(false);
    }
  };

  // ── Payment submission ───────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const paymentAmount = Number(paymentData.amount);
    const remainingDue = Number(selectedInstallment?.dueAmount || 0);
    if (!Number.isFinite(paymentAmount) || paymentAmount <= 0 || paymentAmount > remainingDue) {
      toast.error(`Payment amount must be greater than 0 and no more than the remaining due of ${formatCurrency(remainingDue)}`);
      return;
    }

    try {
      setLoading(true);
      const finalPaymentData = {
        ...paymentData,
        amount:        parseFloat(paymentData.amount),
        installmentNo: parseInt(paymentData.installmentNo),
        paymentDate:   new Date(`${paymentData.paymentDate}T12:00:00`).toISOString(),
      };

      const response = await fetch(`${API_BASE_URL}/fees/record-payment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(finalPaymentData),
      });

      if (!response.ok) throw new Error('Failed to record payment');
      const result = await response.json();

      resetForm();
      onClose();
      if (onPaymentRecorded) onPaymentRecorded(result.payment);
      toast.success('Payment recorded successfully!');
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error(getApiErrorMessage(error, 'Failed to record payment'));
    } finally {
      setLoading(false);
    }
  };

  // ── Reset / close ────────────────────────────────────────────────────────
  const resetForm = () => {
    setStep('student');
    setSearchQuery('');
    setStudents([]);
    setInvoices([]);
    setSelectedStudent(null);
    setSelectedInvoice(null);
    setSelectedInstallment(null);
    selectedStudentRef.current = null;
    setSplitTarget(null);
    setSplitInvoice(null);
    setSplitRows([{ amount: '', dueDate: '' }, { amount: '', dueDate: '' }]);
    setPaymentData({
      invoiceId: '', installmentNo: '', studentId: '',
      amount: '', paymentDate: getLocalDateInputValue(), mode: 'upi',
      referenceNo: '', notes: '', receiptUrl: '',
    });
  };

  const handleFileUpload  = (fileUrl) => setPaymentData(prev => ({ ...prev, receiptUrl: fileUrl }));
  const handleFileRemove  = () => setPaymentData(prev => ({ ...prev, receiptUrl: '' }));
  const handleClose       = () => { resetForm(); onClose(); };

  // ── Formatters ───────────────────────────────────────────────────────────
  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':     return 'text-green-600 bg-green-50';
      case 'pending':  return 'text-yellow-600 bg-yellow-50';
      case 'overdue':  return 'text-red-600 bg-red-50';
      case 'partial':  return 'text-blue-600 bg-blue-50';
      default:         return 'text-gray-600 bg-gray-50';
    }
  };

  // Progress step names (split is a sub-flow, so same 4 main steps shown)
  const STEPS = ['student', 'invoice', 'installment', 'payment'];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="mx-auto max-h-[90vh] w-[95vw] max-w-2xl flex-col overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl">
            {step === 'student'     && 'Select Student'}
            {step === 'invoice'     && 'Select Invoice'}
            {step === 'installment' && 'Select Installment'}
            {step === 'payment'     && 'Record Payment'}
            {step === 'split'       && `Split Installment #${splitTarget?.installmentNo}`}
          </DialogTitle>
          <DialogDescription className="text-sm sm:text-base">
            {step === 'student'     && 'Search and select a student to record payment'}
            {step === 'invoice'     && 'Select the invoice for payment'}
            {step === 'installment' && 'Choose an installment to pay, or split it into smaller parts'}
            {step === 'payment'     && 'Enter payment details and complete the transaction'}
            {step === 'split'       && `Divide ${formatCurrency(splitTarget?.dueAmount || 0)} into smaller installments`}
          </DialogDescription>
        </DialogHeader>

        {/* Progress Steps */}
        {step !== 'split' && (
          <div className="flex items-center justify-between mb-6">
            {STEPS.map((stepName, index) => {
              const isActive    = step === stepName;
              const isCompleted = STEPS.indexOf(step) > index || step === 'payment';
              return (
                <div key={stepName} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-500 ease-out ${
                    isActive    ? 'bg-blue-600 text-white scale-110 shadow-lg shadow-blue-300' :
                    isCompleted ? 'bg-green-600 text-white scale-100' :
                                  'bg-gray-200 text-gray-600 scale-90'}`}>
                    {isCompleted && !isActive
                      ? <span className="animate-[scale-in_0.3s_ease-out]">✓</span>
                      : <span className={isActive ? 'animate-pulse' : ''}>{index + 1}</span>}
                  </div>
                  {index < 3 && (
                    <div className={`w-12 h-1 transition-all duration-500 ease-out ${isCompleted ? 'bg-green-600' : 'bg-gray-200'}`} />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Step: Student ──────────────────────────────────────────────── */}
        {step === 'student' && (
          <div className="space-y-4">
            <div className="relative">
              <Search className={`absolute left-3 top-3 h-4 w-4 text-gray-500 ${searching ? 'animate-pulse' : ''}`} />
              <Input
                type="text"
                placeholder="Search students by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 transition-all duration-300 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {searching ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => <StudentCardSkeleton key={i} />)}
              </div>
            ) : students.length > 0 ? (
              <div className="space-y-2">
                {students.map((student, index) => (
                  <div
                    key={student._id}
                    className="flex items-center p-3 border rounded-lg hover:bg-blue-50 hover:border-blue-300 cursor-pointer transition-all duration-300 ease-out transform hover:scale-[1.02] hover:shadow-md animate-[slide-in_0.3s_ease-out] opacity-0"
                    style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'forwards' }}
                    onClick={() => handleStudentSelect(student)}
                  >
                    <User className="h-5 w-5 text-gray-500 mr-3" />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{student.name}</div>
                      <div className="text-xs text-gray-500">{student.email} • {student.phone}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : searchQuery.length >= 2 && !searching ? (
              <div className="text-center py-8 text-gray-500 animate-[fade-in_0.5s_ease-out]">
                <div className="mb-2">🔍</div>
                No students found matching "{searchQuery}"
              </div>
            ) : null}
          </div>
        )}

        {/* ── Step: Invoice ──────────────────────────────────────────────── */}
        {step === 'invoice' && (
          <div className="space-y-4 animate-[fade-in_0.4s_ease-out]">
            <div className="flex items-center p-3 bg-blue-50 rounded-lg border border-blue-200 animate-[slide-down_0.4s_ease-out]">
              <User className="h-5 w-5 text-blue-600 mr-3" />
              <div>
                <div className="font-medium text-sm">{selectedStudent?.name}</div>
                <div className="text-xs text-blue-600">{selectedStudent?.email}</div>
              </div>
            </div>

            {loading ? (
              <div className="space-y-3">
                <div className="flex flex-col items-center justify-center py-8">
                  <ExpandingCircleLoader />
                  <p className="text-sm text-gray-600 mt-4 animate-pulse">Loading invoices...</p>
                </div>
                {[1, 2, 3].map(i => <InvoiceCardSkeleton key={i} />)}
              </div>
            ) : invoices.filter(inv => inv.hasPendingInstallments).length > 0 ? (
              <div className="space-y-3">
                {invoices.filter(inv => inv.hasPendingInstallments).map((invoice, index) => (
                  <div
                    key={invoice._id}
                    className="p-4 border rounded-lg hover:bg-blue-50 hover:border-blue-300 cursor-pointer transition-all duration-300 ease-out transform hover:scale-[1.01] hover:shadow-lg animate-[slide-in_0.4s_ease-out] opacity-0"
                    style={{ animationDelay: `${index * 100}ms`, animationFillMode: 'forwards' }}
                    onClick={() => handleInvoiceSelect(invoice)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-gray-500" />
                          <span className="font-medium text-sm">{invoice.courseName}</span>
                          <span className={`px-2 py-1 rounded-full text-xs transition-all duration-300 ${getStatusColor(invoice.status)}`}>
                            {invoice.status}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Batch: {invoice.batchName} • Total: {formatCurrency(invoice.totalAmount)}
                        </div>
                        <div className="text-xs text-blue-600 mt-2 font-medium">
                          {invoice.pendingInstallments.length} pending installment(s)
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-green-600">
                          {formatCurrency(
                            invoice.pendingInstallments.reduce((sum, inst) => sum + inst.dueAmount, 0)
                          )}
                        </div>
                        <div className="text-xs text-gray-500">Pending</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 animate-[fade-in_0.5s_ease-out]">
                <div className="mb-2">📋</div>
                No invoices with pending installments found for this student.
              </div>
            )}
          </div>
        )}

        {/* ── Step: Installment ──────────────────────────────────────────── */}
        {step === 'installment' && selectedInvoice && (
          <div className="space-y-4 animate-[fade-in_0.4s_ease-out]">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 animate-[slide-down_0.4s_ease-out]">
              <div className="font-medium text-sm">{selectedStudent?.name}</div>
              <div className="text-xs text-blue-600">
                {selectedInvoice.courseName} • {selectedInvoice.batchName}
              </div>
            </div>

            <div className="space-y-3">
              {selectedInvoice.pendingInstallments.map((installment, index) => (
                <div
                  key={installment.installmentNo}
                  className="p-4 border rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all duration-300 ease-out transform hover:scale-[1.01] hover:shadow-lg animate-[slide-in_0.4s_ease-out] opacity-0"
                  style={{ animationDelay: `${index * 80}ms`, animationFillMode: 'forwards' }}
                >
                  <div className="flex items-center justify-between">
                    {/* Left: installment info */}
                    <div
                      className="flex items-center gap-3 flex-1 cursor-pointer"
                      onClick={() => handleInstallmentSelect(installment, selectedInvoice)}
                    >
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center transition-all duration-300 hover:bg-blue-200 hover:scale-110">
                        <span className="text-sm font-medium text-blue-600">
                          {installment.installmentNo}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-sm">
                          Installment {installment.installmentNo}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Due: {formatDate(installment.dueDate)}
                          </div>
                          <div className="flex items-center gap-1">
                            <IndianRupee className="h-3 w-3" />
                            Original: {formatCurrency(installment.originalAmount)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right: amount + split button */}
                    <div className="flex items-center gap-3">
                      <div
                        className="text-right cursor-pointer"
                        onClick={() => handleInstallmentSelect(installment, selectedInvoice)}
                      >
                        <div className="text-sm font-medium text-green-600">
                          {formatCurrency(installment.dueAmount)}
                        </div>
                        <div className={`text-xs ${getStatusColor(installment.status)} px-2 py-1 rounded-full`}>
                          {installment.status}
                        </div>
                      </div>

                      {/* Split button — only for pending/overdue installments */}
                      {['pending', 'overdue'].includes(installment.status) && (
                        <Button
                          size="sm"
                          variant="outline"
                          title="Split into smaller installments"
                          onClick={(e) => {
                            e.stopPropagation();
                            openSplitStep(installment, selectedInvoice);
                          }}
                          className="flex items-center gap-1 text-xs text-orange-600 border-orange-300 hover:bg-orange-50"
                        >
                          <Scissors className="h-3.5 w-3.5" />
                          Split
                        </Button>
                      )}
                    </div>
                  </div>

                  {installment.paidAmount > 0 && (
                    <div className="text-xs text-gray-500 mt-2">
                      Already paid: {formatCurrency(installment.paidAmount)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Step: Split ────────────────────────────────────────────────── */}
        {step === 'split' && splitTarget && (
          <div className="space-y-4 animate-[fade-in_0.4s_ease-out]">
            {/* Info banner */}
            <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm text-orange-800">
                    Splitting Installment #{splitTarget.installmentNo}
                  </div>
                  <div className="text-xs text-orange-600 mt-0.5">
                    {splitInvoice?.courseName} • Due: {formatDate(splitTarget.dueDate)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-orange-700">
                    {formatCurrency(splitTarget.dueAmount)}
                  </div>
                  <div className="text-xs text-orange-500">Total to split</div>
                </div>
              </div>
            </div>

            {/* Running total validation */}
            <div className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              splitValid ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              <span>Total entered</span>
              <span>
                {formatCurrency(splitTotal)} / {formatCurrency(splitTarget.dueAmount)}
                {!splitValid && ` (${splitDiff > 0 ? '+' : ''}${(splitTotal - splitTarget.dueAmount).toFixed(2)} off)`}
              </span>
            </div>

            {/* Split rows */}
            <div className="space-y-3">
              {splitRows.map((row, idx) => (
                <div
                  key={idx}
                  className="flex items-end gap-2 p-3 border rounded-lg bg-gray-50 animate-[slide-in_0.3s_ease-out]"
                >
                  <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mb-1">
                    <span className="text-xs font-medium text-orange-600">{idx + 1}</span>
                  </div>

                  <div className="flex-1 space-y-1">
                    <Label className="text-xs text-gray-500">Amount (₹)</Label>
                    <Input
                      type="number"
                      min="0.01"
                      step="0.01"
                      placeholder="0.00"
                      value={row.amount}
                      onChange={(e) => updateSplitRow(idx, 'amount', e.target.value)}
                      className="text-sm h-8"
                    />
                  </div>

                  <div className="flex-1 space-y-1">
                    <Label className="text-xs text-gray-500">Due Date</Label>
                    <Input
                      type="date"
                      value={row.dueDate}
                      onChange={(e) => updateSplitRow(idx, 'dueDate', e.target.value)}
                      className="text-sm h-8"
                    />
                  </div>

                  {splitRows.length > 2 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removeSplitRow(idx)}
                      className="text-red-400 hover:text-red-600 hover:bg-red-50 mb-0.5 h-8 w-8 p-0 flex-shrink-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addSplitRow}
              className="w-full border-dashed text-gray-500 hover:text-blue-600 hover:border-blue-400"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add another split
            </Button>

            <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setSplitTarget(null); setSplitInvoice(null); setStep('installment'); }}
                disabled={splitting}
                className="w-full sm:w-auto"
              >
                Back
              </Button>
              <Button
                type="button"
                onClick={handleSplitSubmit}
                disabled={splitting || !splitValid}
                className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-60"
              >
                {splitting ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Splitting...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Scissors className="h-4 w-4" />
                    Confirm Split
                  </div>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* ── Step: Payment ──────────────────────────────────────────────── */}
        {step === 'payment' && (
          <form onSubmit={handleSubmit} className="space-y-4 animate-[fade-in_0.5s_ease-out]">
            {/* Summary Card */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200 animate-[slide-down_0.5s_ease-out] shadow-sm">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div className="transform transition-all duration-300 hover:scale-105">
                  <div className="text-gray-500 text-xs">Student</div>
                  <div className="font-medium">{selectedStudent?.name}</div>
                </div>
                <div className="transform transition-all duration-300 hover:scale-105">
                  <div className="text-gray-500 text-xs">Course</div>
                  <div className="font-medium">{selectedInvoice?.courseName}</div>
                </div>
                <div className="transform transition-all duration-300 hover:scale-105">
                  <div className="text-gray-500 text-xs">Installment</div>
                  <div className="font-medium">#{selectedInstallment?.installmentNo}</div>
                </div>
                <div className="sm:col-span-3 transform transition-all duration-300 hover:scale-105">
                  <div className="text-gray-500 text-xs">Amount Due</div>
                  <div className="text-xl font-bold text-green-600 flex items-center gap-1">
                    <IndianRupee className="h-5 w-5" />
                    {formatCurrency(selectedInstallment?.dueAmount || 0)}
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Form */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2 animate-[slide-in_0.6s_ease-out]">
                <Label htmlFor="amount" className="text-sm">Amount *</Label>
                <Input
                  id="amount"
                  type="number"
                  min="0.01"
                  max={selectedInstallment?.dueAmount}
                  step="0.01"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                  placeholder="Enter amount"
                  required
                  className="text-sm transition-all duration-300 focus:ring-2 focus:ring-blue-500 focus:scale-[1.02]"
                />
                <p className="text-xs text-gray-500">
                  Remaining installment due: {formatCurrency(selectedInstallment?.dueAmount || 0)}
                </p>
              </div>

              <div className="space-y-2 animate-[slide-in_0.7s_ease-out]">
                <Label htmlFor="mode" className="text-sm">Payment Mode *</Label>
                <Select value={paymentData.mode} onValueChange={(v) => setPaymentData({ ...paymentData, mode: v })}>
                  <SelectTrigger className="text-sm transition-all duration-300 focus:ring-2 focus:ring-blue-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="transfer">Bank Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 animate-[slide-in_0.75s_ease-out]">
                <Label htmlFor="paymentDate" className="text-sm">Payment Date *</Label>
                <Input
                  id="paymentDate"
                  type="date"
                  value={paymentData.paymentDate}
                  onChange={(e) => setPaymentData({ ...paymentData, paymentDate: e.target.value })}
                  required
                  className="text-sm"
                />
              </div>

              <div className="space-y-2 animate-[slide-in_0.8s_ease-out]">
                <Label htmlFor="referenceNo" className="text-sm">Reference No</Label>
                <Input
                  id="referenceNo"
                  value={paymentData.referenceNo}
                  onChange={(e) => setPaymentData({ ...paymentData, referenceNo: e.target.value })}
                  placeholder="Transaction reference"
                  className="text-sm transition-all duration-300 focus:ring-2 focus:ring-blue-500 focus:scale-[1.02]"
                />
              </div>
            </div>

            <div className="space-y-2 animate-[slide-in_0.9s_ease-out]">
              <Label htmlFor="notes" className="text-sm">Notes</Label>
              <Textarea
                id="notes"
                value={paymentData.notes}
                onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                placeholder="Additional notes about this payment"
                rows={3}
                className="text-sm resize-none transition-all duration-300 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="space-y-2 animate-[slide-in_1s_ease-out]">
              <Label className="text-sm">Payment Receipt</Label>
              <FileUpload
                onFileUpload={handleFileUpload}
                onFileRemove={handleFileRemove}
                currentFileUrl={paymentData.receiptUrl}
                accept="image/*,application/pdf"
                maxSize={10 * 1024 * 1024}
              />
            </div>

            <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep('installment')}
                className="w-full sm:w-auto order-2 sm:order-1 transition-all duration-300 hover:scale-105"
                disabled={loading}
              >
                Back
              </Button>
              <Button
                type="submit"
                className="w-full sm:w-auto order-1 sm:order-2 transition-all duration-300 hover:scale-105 hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Recording...
                  </div>
                ) : 'Record Payment'}
              </Button>
            </DialogFooter>
          </form>
        )}

        {/* Back / Cancel for non-terminal steps */}
        {step !== 'payment' && step !== 'student' && step !== 'split' && (
          <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (step === 'invoice')     { setStep('student'); setInvoices([]); }
                else if (step === 'installment') { setStep('invoice'); setSelectedInstallment(null); }
              }}
              className="w-full sm:w-auto transition-all duration-300 hover:scale-105"
            >
              Back
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="w-full sm:w-auto transition-all duration-300 hover:scale-105"
            >
              Cancel
            </Button>
          </DialogFooter>
        )}

        <style>{`
          @keyframes shimmer {
            0%   { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          @keyframes slide-in {
            from { opacity: 0; transform: translateX(-20px); }
            to   { opacity: 1; transform: translateX(0); }
          }
          @keyframes slide-down {
            from { opacity: 0; transform: translateY(-20px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @keyframes fade-in {
            from { opacity: 0; }
            to   { opacity: 1; }
          }
          @keyframes scale-in {
            from { transform: scale(0); }
            to   { transform: scale(1); }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

export default RecordPaymentDialog;
